import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import InstallPrompt from "./components/InstallPrompt";
import ProtectedRoute from "./components/admin/ProtectedRoute";
import AppVersionChecker from "./components/AppVersionChecker";
import { CountrySelector } from "./components/CountrySelector";
import VisitorTracker from "./components/VisitorTracker";
import { ThemeProvider } from "./components/ThemeProvider";

// Lazy load non-critical routes
const ProductPage = lazy(() => import("./pages/ProductPage"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const PaymentMethodPage = lazy(() => import("./pages/PaymentMethodPage"));
const QuickPaymentPage = lazy(() => import("./pages/QuickPaymentPage"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const SizeGuidePage = lazy(() => import("./pages/SizeGuidePage"));
const ReturnPolicyPage = lazy(() => import("./pages/ReturnPolicyPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const CasePage = lazy(() => import("./pages/CasePage"));
const CasesPage = lazy(() => import("./pages/CasesPage"));
const TrackOrderPage = lazy(() => import("./pages/TrackOrderPage"));

// Lazy load admin routes
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const DeliveryLogin = lazy(() => import("./pages/admin/DeliveryLogin"));
const AdminLedger = lazy(() => import("./pages/admin/AdminLedger"));
const AdminCases = lazy(() => import("./pages/admin/AdminCases"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminAssistant = lazy(() => import("./pages/admin/AdminAssistant"));
const MobileOrders = lazy(() => import("./pages/MobileOrders"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner position="top-center" closeButton richColors />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Store Routes */}
            <Route path="/" element={<Index />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/cases" element={<CasesPage />} />
            <Route path="/case/:id" element={<CasePage />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/categories/:id" element={<CategoriesPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/payment-method" element={<PaymentMethodPage />} />
            <Route path="/quick-payment" element={<QuickPaymentPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order-success/:orderNumber" element={<OrderSuccessPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/size-guide" element={<SizeGuidePage />} />
            <Route path="/return-policy" element={<ReturnPolicyPage />} />
            <Route path="/track-order" element={<TrackOrderPage />} />
            
            {/* Auth Routes */}
            <Route path="/auth" element={<AuthPage />} />
            
            
            {/* Admin Routes - Protected */}
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/orders" element={<ProtectedRoute><AdminOrders /></ProtectedRoute>} />
            <Route path="/admin/products" element={<ProtectedRoute><AdminProducts /></ProtectedRoute>} />
            <Route path="/admin/categories" element={<ProtectedRoute><AdminCategories /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/delivery-login" element={<ProtectedRoute><DeliveryLogin /></ProtectedRoute>} />
            <Route path="/admin/ledger" element={<ProtectedRoute><AdminLedger /></ProtectedRoute>} />
            <Route path="/admin/cases" element={<ProtectedRoute><AdminCases /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
            <Route path="/admin/assistant" element={<ProtectedRoute><AdminAssistant /></ProtectedRoute>} />
            
            {/* Mobile PWA Orders */}
            <Route path="/orders" element={<ProtectedRoute><MobileOrders /></ProtectedRoute>} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <VisitorTracker />
        <InstallPrompt />
        <AppVersionChecker />
        {/* Country Selector Modal - shows on first visit */}
        <CountrySelector />
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
